import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';

import { MenuCategory } from '../models/category.model';
import { CategorySections, StyleHeaderEnum } from '../models/header.models';

import { AttachmentsEnums } from './attachments.enums';
import { AttachmentsUtilsService } from './attachments.utils.service';

import { applicationHomePagesMap } from '@core/domain/constants/application-pages.const';
import { ApplicationSections } from '@core/domain/models/application-section.model';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { SidebarPublicModels, SidebarPublicPort } from '@features/sidebar';
import { checkMatch, deepClone } from '@frontend/common/utils';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { HeaderEventAnalytics } from '@shared/analytics/store/app-events-analytics.actions';
import {
    NavigateToCategoryPageAnalytics,
    SetActiveCategoryIdLvl1,
    OpenMenu,
} from '@shared/menu-categories/store/menu-categories.actions';
import { ActiveCategoriesTree } from '@shared/menu-categories/store/menu-categories.state';
import { GoToCategory, GoToSection } from '@shared/navigation/store/navigation.actions';
import { NavigationService } from '@shared/services/navigation/navigation.service';

@Injectable({
    providedIn: 'root',
})
export class HeaderUtilsService {
    constructor(
        private readonly store: Store,
        private readonly navigationService: NavigationService,
        // eslint-disable-next-line jira-ticket-todo-comment/jira-ticket-todo-comment
        private readonly abTestingService: ABTestingService, // TODO QUITAR CON CAMBIO DE ABEL
        @Inject(WINDOW) private readonly window: CommonWindow
    ) {
    }

    public static getIndexBySection(_section: string): number {
        return CategorySections.findIndex(section => section === _section);
    }

    public static getSectionByHomePage(_page: string): ApplicationSections {
        if (_page === 'ItxHomePage' || _page === 'ItxHomeWomenPage') {
            return ApplicationSections.WOMEN;
        }
        if (_page === 'ItxHomeMenPage') {
            return ApplicationSections.MEN;
        }
        if (_page === 'ItxHomeBgPage') {
            return ApplicationSections.BG;
        }
        return null;
    }

    // eslint-disable-next-line jira-ticket-todo-comment/jira-ticket-todo-comment
    // TODO QUITAR CON CAMBIO DE ABEL
    public openShopCart() {
        this.store.dispatch(HeaderEventAnalytics({ section: 'cart' }));
        // Test AB ECOMDUTI-16388 Reducir pasos minicesta
        if (this.abTestingService.getShopCartStepVariant('goToShopCartFromHeaderButton')) {
            this.store.dispatch(GoToSection({ url: 'shopCart' }));
        } else {
            SidebarPublicPort(this.store).openSidebar(SidebarPublicModels.view.MINI_CART);
        }
    }

    public static getHomePageBySection(_section: string) {
        if (_section === ApplicationSections.WOMEN) return 'ItxHomePage';
        if (_section === ApplicationSections.MEN) return 'ItxHomeMenPage';
        if (_section === ApplicationSections.BG) return 'ItxHomeBgPage';
        return null;
    }

    public static isChildOfCategory(category: MenuCategory, id, level = 0, maxlevel = 3): boolean {
        if (category === undefined || id === undefined) {
            return false;
        }

        if (level > maxlevel) {
            return false;
        }

        if (category.id === id || category.redirectToCategoryId === id) {
            return true;
        }

        if (!!category.subcategories && category.subcategories.length > 0) {
            return !!category.subcategories
                .find(subCategory => this.isChildOfCategory(subCategory, id, level + 1, maxlevel));
        }

        return false;
    }

    public static getSectionByIndex(index: number): string {
        return CategorySections[index];
    }

    public isHomePage(page: string): boolean {
        return checkMatch(page, applicationHomePagesMap);
    }

    public isCategoryPage(page: string): boolean {
        return page === 'ItxProductPage' || page === 'ItxCategoryPage';
    }

    public openCategory(category: MenuCategory, index: number): void {
        this.store.dispatch(NavigateToCategoryPageAnalytics({ category }));
        let categoryId = category.redirectToCategoryId ?? category.id;
        // Navegación teniendo subcategorias y con redirect
        if (category.isInformativeClickable && !category.isExternalUrl && category.redirectToCategoryId) {
            this.store.dispatch(GoToCategory({ categoryId }));
        }
        // Navegación sin subcategorias desde API
        if (!category.isInformativeClickable && !category.isExternalUrl) {
            this.store.dispatch(GoToCategory({ categoryId }));
        }
        // Navegación con subcategorias de API y ocultas por attachment
        if (!category.subcategories.length && !category?.isExternalUrl) {
            this.store.dispatch(GoToCategory({ categoryId }));
        }
        if (category.isExternalUrl) {
            this.window.location.href = category.locationPath;
        }
        this.store.dispatch(SetActiveCategoryIdLvl1({ activeCategoryIdLvl1: category.id, index }));
    }

    public openSubCategory(category: MenuCategory): void {
        this.store.dispatch(NavigateToCategoryPageAnalytics({ category }));
        let categoryId = category.redirectToCategoryId ?? category.id;
        // NO NAVEGACION
        if (category.isInformativeClickable && !categoryId) {
            return;
        }
        // NAVEGACION PAGINA INTERNA CON ID
        if (!category.isExternalUrl && !!!category.redirectToCategoryId) {
            this.store.dispatch(GoToCategory({ categoryId: category.id }));
        }

        // NAVEGACION PAGINA INTERNA CON REDIRECT-ID
        if (!category.isExternalUrl && !!category.redirectToCategoryId) {
            this.store.dispatch(GoToCategory({ categoryId: category.redirectToCategoryId }));
            return;
        }

        // NAVEGACION PAGINA EXTERNA
        if (category.isExternalUrl && !!!category.redirectToCategoryId) {
            this.navigationService.redirect(category.locationPath, false);
            return;
        }
        // NAVEGACION POR DEFECTO
        this.store.dispatch(GoToCategory({ categoryId: categoryId }));
    }

    public static removeCategoriesHidden(categories: MenuCategory[]): MenuCategory[] {
        return categories
            .filter(category => !category.isMenuHidden
                && !category.attachments
                    ?.find((att) => att.name === AttachmentsEnums.GAUDI || att.name === AttachmentsEnums.HIDDEN))
            .map(category => {
                if (category.subcategories?.length > 0) {
                    const _category = deepClone(category);
                    _category.subcategories = this.removeCategoriesHidden(category.subcategories);
                    return _category;
                }
                return category;
            });
    }

    public static searchRecursiveCategoryById(categories: MenuCategory[], id: string): MenuCategory {
        if (categories?.length > 0) {
            let category = categories.find((cat: any) => cat.id === id || cat.redirectToCategoryId === id);
            if (!!!category) {
                for (let cat of categories) {
                    const _category = this.searchRecursiveCategoryById(cat.subcategories, id);
                    if (!!_category) {
                        category = _category;
                    }
                }
            }
            return category;
        }
        return undefined;
    }

    public isIconBlack(theme): boolean {
        return [
            StyleHeaderEnum.COMPACT,
            StyleHeaderEnum.PHANTOM_DARK,
        ].some(el => theme.includes(el));
    }

    public openMenuByCategoryId(activeCategoryIdLvl1: string, index: number): void {
        this.store.dispatch(SetActiveCategoryIdLvl1({ activeCategoryIdLvl1, index }));
        this.store.dispatch(OpenMenu());
    }

    /* si la categoria es: camisetas sin mangas
    *   entonces esta funcion retornara
    * * seccion -> WOMEN
    * * categoria -> COLLECCION
    * * subcategoria -> CAMISETAS
    * * thirdcategoria -> CAMISETAS SIN MANGAS
    */
    public static getCategoriesLevelsById(id: string, menuCategories: MenuCategory[]): Record<number, ActiveCategoriesTree> {
        let activeCategories = {};
        for (let i = 0; i <= menuCategories.length - 1; i++) {
            activeCategories[i] = this.getCategoryLevelsById(id, menuCategories, i);
        }
        return activeCategories;
    }

    public static getCategoryLevelsById(
        id: string,
        menuCategories: MenuCategory[],
        indexCategory: number
    ): ActiveCategoriesTree {
        const menuCategory: MenuCategory = HeaderUtilsService
            .searchRecursiveCategoryById(menuCategories, id);

        // si la id no pertenece a ningun padre definimos WOMEN por defecto
        let _indexCategory = indexCategory === -1 ? 0 : indexCategory;

        // search category Menu 1
        let firstLevelCategory = menuCategories[_indexCategory].subcategories.find(
            category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.id)
        );
        if (firstLevelCategory === undefined) {
            firstLevelCategory = menuCategories[_indexCategory].subcategories.find(
                category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.redirectToCategoryId)
            );
        }
        const defaultActiveCategoryLvl1 = menuCategories[_indexCategory].subcategories.find(
            category => AttachmentsUtilsService.checkAttachmentName(category, AttachmentsEnums.SHOW_SUBCATEGORIES)
        );

        // search category Menu 2
        let secondLevelCategory;
        if (!!firstLevelCategory && menuCategory?.id) {
            secondLevelCategory = firstLevelCategory.subcategories.find(
                category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.id)
            );
            if (secondLevelCategory === undefined) {
                secondLevelCategory = firstLevelCategory.subcategories.find(
                    category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.redirectToCategoryId)
                );
            }
        }
        // search category Menu 3
        let thirdLevelCategory;
        if (!!secondLevelCategory && menuCategory?.id) {
            thirdLevelCategory = secondLevelCategory.subcategories?.find(
                category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.id)
            );
            if (thirdLevelCategory === undefined) {
                thirdLevelCategory = secondLevelCategory.subcategories?.find(
                    category => HeaderUtilsService.isChildOfCategory(category, menuCategory?.redirectToCategoryId)
                );
            }
        }

        return {
            activeCategoryIdLvl1: firstLevelCategory?.id ?? defaultActiveCategoryLvl1?.id,
            activeCategoryIdLvl2: secondLevelCategory?.id,
            activeCategoryIdLvl3: thirdLevelCategory?.id,
        };
    }

    public static getDefaultActiveCategory(section: string, menuCategories: MenuCategory[]): MenuCategory {
        const index = HeaderUtilsService.getIndexBySection(section);
        const category = menuCategories[index];
        return category.subcategories.find(cat =>
            !cat.isMenuHidden
            && AttachmentsUtilsService.checkAttachmentName(cat, AttachmentsEnums.SHOW_SUBCATEGORIES));
    }

    public static headerThemeIsPhantom(styleHeader: StyleHeaderEnum | string): boolean {
        return [
            StyleHeaderEnum.PHANTOM_WHITE_GRADIENT,
            StyleHeaderEnum.PHANTOM_WHITE,
            StyleHeaderEnum.PHANTOM_DARK,
            StyleHeaderEnum.BLEND,
        ].includes(styleHeader as any);
    }
}
